import {
    MODEL_QUOTA_NO_LIMIT,
    ModelComputeFrequency,
} from "models/ModelCompute";
import type { ILLMExecutorStep } from "models/LLMs/LogicChaining/LLMExecutorStep";
import type { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";
import {
    LLMClassifierVersionQuota,
    LLMClassifierVersionSettings,
    LLMClassifierImplementationConfigurationType,
} from "models/LLMs/LLMClassifierImplementation";
import { LLMClassifierPurpose } from "models/LLMs/LLMClassifierPurpose";
import type { LLMResultDefinition } from "models/LLMs/LLMResultDefinition";
import { LLMExecutorStepPromptConfigurationOutputVariableType } from "models/LLMs/LogicChaining/LLMExecutorStepPromptConfiguration";
import type { ILLMExecutorStepPromptConfigurationOutputVariable } from "models/LLMs/LogicChaining/LLMExecutorStepPromptConfiguration";
import { deepCamelCase } from "../utils";
import {
    partitionExecuteConditions,
    stripVolatileNestedFields,
} from "./llmClassifierConditionGrouping";
import { getLineDiffCounts } from "./lineDiffCounts";

// Same stop keys as LLMClassifierLogicChainBuilder, so nested schemas survive.
const CONFIGURATION_STOP_KEYS = [
    "inputVariables",
    "structuredOutputJsonSchema",
];

const NONE = "None";
const UNNAMED_PARENT = "Unnamed model";
const UNKNOWN_HIERARCHY = "Unavailable hierarchy";

export type PromptBlockKind = "changed" | "added" | "removed" | "renamed";

export interface PromptDiffBlock {
    key: string;
    /** Step name; undefined for the single prompt of a standard model. */
    label?: string;
    /** 1-based position in the chain; undefined for implementation-level blocks. */
    stepNumber?: number;
    /** Identity for grouping. Names are neither unique nor stable. */
    stepId?: string;
    kind: PromptBlockKind;
    beforeText: string;
    afterText: string;
    addedLines: number;
    removedLines: number;
}

export interface MetadataChange {
    key: string;
    label: string;
    /** Step name for per-step rows; undefined for implementation-level rows. */
    stepLabel?: string;
    stepNumber?: number;
    stepId?: string;
    before: string;
    after: string;
}

/** A nested configuration structure (conditions, actions, schemas), rendered as a text diff since it won't fit metadata rows' before/after chips. */
export interface ConfigDiffBlock {
    key: string;
    label: string;
    /** Step name for per-step blocks; undefined for implementation-level ones. */
    stepLabel?: string;
    stepNumber?: number;
    stepId?: string;
    beforeText: string;
    afterText: string;
    addedLines: number;
    removedLines: number;
}

export interface VersionComparison {
    isBuilderTypeChange: boolean;
    promptBlocks: PromptDiffBlock[];
    metadataChanges: MetadataChange[];
    configBlocks: ConfigDiffBlock[];
    hasChanges: boolean;
    /** Sides whose version predates settings being recorded; left out of the comparison rather than reported as removed. */
    missingSettingsSides: ("before" | "after")[];
}

/** Hierarchy names the caller has already resolved, keyed by id. */
export interface VersionComparisonOptions {
    hierarchyNameById?: Record<string, string>;
}

interface NormalizedStep {
    id: string;
    /** Position in the chain as saved, used to order and number the groups. */
    index: number;
    name: string;
    prompt: string;
    llmModelName?: string;
    temperature?: number | null;
    thinkingEffortLevel?: string | null;
    isEntryPoint: boolean;
    actions: unknown[];
    expectedOutputs: ILLMExecutorStepPromptConfigurationOutputVariable[];
    useStructuredOutputs: boolean;
    structuredOutputJsonSchema?: unknown;
}

interface NormalizedSettings {
    name: string;
    description: string;
    groupId?: string;
    groupLabel: string;
    visibility: string;
    roleLabel: string;
    outputs: { id: string; result: string }[];
    quotaLines: string[];
}

interface NormalizedVersion {
    type?: LLMClassifierImplementationConfigurationType;
    promptTemplate: string;
    questionText: string;
    questionDescription: string;
    steps: NormalizedStep[];
    llmModelLabel: string;
    llmPurpose?: LLMClassifierPurpose;
    llmTargetSpeaker?: string;
    resultDefinitions: LLMResultDefinition[];
    runConditions: unknown[];
    metadataFilters: unknown[];
    /** Undefined when the version predates settings being recorded. */
    settings?: NormalizedSettings;
    parentModels: { classifierId: string; name: string }[];
}

export function getLLMClassifierVersionComparison(
    before?: LLMClassifierImplementation,
    after?: LLMClassifierImplementation,
    options?: VersionComparisonOptions,
): VersionComparison | undefined {
    if (!before || !after) return undefined;

    // Each side uses its own recorded hierarchy names, not the caller's live (permission-filtered) list.
    const from = normalizeVersion(before, {
        ...options,
        hierarchyNameById: {
            ...before.settings?.hierarchyNames,
            ...options?.hierarchyNameById,
        },
    });
    const to = normalizeVersion(after, {
        ...options,
        hierarchyNameById: {
            ...after.settings?.hierarchyNames,
            ...options?.hierarchyNameById,
        },
    });
    const isBuilderTypeChange = from.type !== to.type;

    const promptBlocks = isBuilderTypeChange
        ? buildReplacementBlocks(from, to)
        : buildPromptBlocks(from, to);
    const metadataChanges = buildMetadataChanges(from, to, promptBlocks);
    const configBlocks = buildConfigBlocks(from, to, promptBlocks);

    return {
        isBuilderTypeChange,
        promptBlocks,
        metadataChanges,
        configBlocks,
        // Missing settings alone don't count as a change, or an old pair with none would render blank groups instead of "no changes".
        hasChanges:
            promptBlocks.length > 0 ||
            metadataChanges.length > 0 ||
            configBlocks.length > 0,
        missingSettingsSides: [
            ...(from.settings ? [] : ["before" as const]),
            ...(to.settings ? [] : ["after" as const]),
        ],
    };
}

function normalizeVersion(
    version: LLMClassifierImplementation,
    options?: VersionComparisonOptions,
): NormalizedVersion {
    // Configuration is stored as a raw JObject, so it arrives PascalCased.
    const configuration = deepCamelCase(
        version.configuration ?? {},
        CONFIGURATION_STOP_KEYS,
    ) as Record<string, unknown>;

    const steps = Array.isArray(configuration.steps)
        ? (configuration.steps as ILLMExecutorStep[]).map(normalizeStep)
        : [];
    // executeConditionsV2 is write-only today; the editor reads executeConditions.
    const conditions = partitionExecuteConditions(
        configuration.executeConditions,
    );

    return {
        type: version.type,
        promptTemplate: asText(configuration.promptTemplate),
        questionText: asText(configuration.questionText),
        questionDescription: asText(configuration.questionDescription),
        steps,
        llmModelLabel: version.llmModelName ?? version.llmModelId ?? NONE,
        llmPurpose: version.llmPurpose,
        llmTargetSpeaker: version.llmTargetSpeaker,
        resultDefinitions: version.llmResultDefinitions ?? [],
        settings: version.settings
            ? normalizeSettings(version.settings, options)
            : undefined,
        runConditions: conditions.runConditions,
        metadataFilters: conditions.metadataFilters,
        parentModels: (version.parentModels ?? []).map((parent) => ({
            classifierId: parent.classifierId,
            name: parent.name || UNNAMED_PARENT,
        })),
    };
}

function normalizeStep(step: ILLMExecutorStep, index: number): NormalizedStep {
    return {
        id: step.id,
        index,
        name: step.name,
        prompt: asText(step.configuration?.prompt),
        llmModelName: step.llmModelName,
        temperature: step.modelOptions?.temperature,
        thinkingEffortLevel: step.modelOptions?.thinkingEffortLevel,
        isEntryPoint: step.isEntryPoint,
        actions: step.configuration?.actions ?? [],
        expectedOutputs: step.configuration?.expectedOutputs ?? [],
        useStructuredOutputs: step.configuration?.useStructuredOutputs ?? false,
        structuredOutputJsonSchema:
            step.configuration?.structuredOutputJsonSchema,
    };
}

/** Matched on id, then name, then slug: a step's id is its name slugged plus a random suffix, regenerated on rename. */
function matchSteps(before: NormalizedStep[], after: NormalizedStep[]) {
    const used = new Set<number>();
    const pairs: { before?: NormalizedStep; after: NormalizedStep }[] = [];

    const take = (predicate: (candidate: NormalizedStep) => boolean) => {
        const index = before.findIndex(
            (candidate, position) =>
                !used.has(position) && predicate(candidate),
        );
        if (index === -1) return undefined;
        used.add(index);
        return before[index];
    };

    for (const step of after) {
        const match =
            take((candidate) => candidate.id === step.id) ??
            take((candidate) => sameName(candidate.name, step.name)) ??
            take((candidate) => slugBase(candidate.id) === slugBase(step.id));
        pairs.push({ before: match, after: step });
    }

    const unmatchedBefore = before.filter((_, index) => !used.has(index));
    return { pairs, unmatchedBefore };
}

/** Callers must agree on which steps pair up, or a renamed step reports its rename and drops its other changes. */
function matchStepsWithRenames(
    before: NormalizedStep[],
    after: NormalizedStep[],
) {
    const { pairs, unmatchedBefore } = matchSteps(before, after);
    const added = pairs.filter((pair) => !pair.before);

    // One step in and one out is far more often a rename than an unrelated swap.
    if (added.length === 1 && unmatchedBefore.length === 1) {
        added[0].before = unmatchedBefore.pop();
    }

    return { pairs, unmatchedBefore };
}

function buildPromptBlocks(
    from: NormalizedVersion,
    to: NormalizedVersion,
): PromptDiffBlock[] {
    if (to.type === LLMClassifierImplementationConfigurationType.PromptOnly) {
        if (from.promptTemplate === to.promptTemplate) return [];
        return [
            promptBlock(
                "prompt",
                undefined,
                "changed",
                from.promptTemplate,
                to.promptTemplate,
            ),
        ];
    }

    const { pairs, unmatchedBefore } = matchStepsWithRenames(
        from.steps,
        to.steps,
    );

    const blocks: PromptDiffBlock[] = [];
    for (const { before: fromStep, after: toStep } of pairs) {
        if (!fromStep) {
            blocks.push(
                promptBlock(
                    toStep.id,
                    toStep.name,
                    "added",
                    "",
                    toStep.prompt,
                    toStep.index + 1,
                    toStep.id,
                ),
            );
            continue;
        }

        const renamed = !sameName(fromStep.name, toStep.name);
        if (fromStep.prompt === toStep.prompt && !renamed) continue;

        blocks.push(
            promptBlock(
                toStep.id,
                renamed ? `${fromStep.name} → ${toStep.name}` : toStep.name,
                renamed ? "renamed" : "changed",
                fromStep.prompt,
                toStep.prompt,
                toStep.index + 1,
                toStep.id,
            ),
        );
    }

    for (const fromStep of unmatchedBefore) {
        blocks.push(
            promptBlock(
                fromStep.id,
                fromStep.name,
                "removed",
                fromStep.prompt,
                "",
                fromStep.index + 1,
                fromStep.id,
            ),
        );
    }

    return blocks;
}

/** A builder type change replaces every prompt, so pairing them is meaningless. */
function buildReplacementBlocks(
    from: NormalizedVersion,
    to: NormalizedVersion,
): PromptDiffBlock[] {
    const removed = promptsOf(from).map((prompt) =>
        replacementBlock(prompt, "removed", prompt.text, ""),
    );
    const added = promptsOf(to).map((prompt) =>
        replacementBlock(prompt, "added", "", prompt.text),
    );

    return [...added, ...removed];
}

/** The PromptOnly side's prompt has no step of its own, so it merges into the implementation group. */
function replacementBlock(
    prompt: ReturnType<typeof promptsOf>[number],
    kind: "added" | "removed",
    beforeText: string,
    afterText: string,
): PromptDiffBlock {
    const isImplementationPrompt = prompt.label === undefined;

    return promptBlock(
        `${kind}-${prompt.key}`,
        prompt.label,
        kind,
        beforeText,
        afterText,
        prompt.stepNumber,
        isImplementationPrompt ? undefined : `${kind}-${prompt.key}`,
    );
}

function promptsOf(version: NormalizedVersion) {
    if (
        version.type === LLMClassifierImplementationConfigurationType.PromptOnly
    ) {
        return [
            {
                key: "prompt",
                label: undefined,
                text: version.promptTemplate,
                stepNumber: undefined,
            },
        ];
    }
    return version.steps.map((step) => ({
        key: step.id,
        label: step.name,
        text: step.prompt,
        stepNumber: step.index + 1,
    }));
}

function promptBlock(
    key: string,
    label: string | undefined,
    kind: PromptBlockKind,
    beforeText: string,
    afterText: string,
    stepNumber?: number,
    stepId?: string,
): PromptDiffBlock {
    return {
        key,
        label,
        stepNumber,
        stepId,
        kind,
        beforeText,
        afterText,
        ...getLineDiffCounts(beforeText, afterText),
    };
}

function normalizeSettings(
    settings: LLMClassifierVersionSettings,
    options?: VersionComparisonOptions,
): NormalizedSettings {
    const roleNames = (settings.rolesAccessControls ?? []).map(
        (role, index) => settings.roleNames?.[index] ?? role.roleId,
    );

    return {
        name: settings.name ?? "",
        description: settings.description ?? "",
        groupId: settings.classifierGroupId,
        groupLabel:
            settings.classifierGroupName ?? settings.classifierGroupId ?? NONE,
        visibility: visibilityLabel(settings),
        // Sorted so a different saved order is not reported as a change.
        roleLabel: roleNames.length
            ? [...roleNames].sort((a, b) => a.localeCompare(b)).join(", ")
            : NONE,
        outputs: (settings.outputs ?? []).map((output) => ({
            id: output.id,
            result: output.result ?? "",
        })),
        quotaLines: (settings.modelComputeQuotas ?? [])
            .map((quota) => quotaLine(quota, options))
            .sort((a, b) => a.localeCompare(b)),
    };
}

function visibilityLabel(settings: LLMClassifierVersionSettings): string {
    if (settings.isInternal) return "Authenticx only";
    return (settings.rolesAccessControls ?? []).length > 0
        ? "Specific roles"
        : "Anyone";
}

/** Mirrors the sentence the Volume Control editor renders, so the diff reads the way the tab does. */
function quotaLine(
    quota: LLMClassifierVersionQuota,
    options?: VersionComparisonOptions,
): string {
    const limit =
        quota.modelQuota === MODEL_QUOTA_NO_LIMIT
            ? "No conversation limit"
            : `Up to ${quota.modelQuota.toLocaleString()} ${quota.modelQuota === 1 ? "conversation" : "conversations"
            }`;

    const ids = quota.organizationStructureMemberIds ?? [];
    // An empty selection means the quota applies everywhere, not nowhere.
    const where = ids.length
        ? [...ids]
            .map((id) => hierarchyLabel(id, options?.hierarchyNameById ?? {}))
            .sort((a, b) => a.localeCompare(b))
            .join(", ")
        : "all hierarchies";

    return `${limit} in ${where} per ${frequencyLabel(quota.quotaFrequency)}`;
}

/** Hierarchy member names aren't unique -- append the id when the name is ambiguous or unresolved. */
function hierarchyLabel(
    id: string,
    hierarchyNameById: Record<string, string>,
): string {
    const name = hierarchyNameById[id];

    if (!name) return `${UNKNOWN_HIERARCHY} (${id})`;

    const nameIsAmbiguous = Object.entries(hierarchyNameById).some(
        ([candidateId, candidateName]) =>
            candidateId !== id && candidateName === name,
    );

    return nameIsAmbiguous ? `${name} (${id})` : name;
}

function frequencyLabel(frequency: number): string {
    if (frequency === ModelComputeFrequency.Daily) return "day";
    if (frequency === ModelComputeFrequency.Weekly) return "week";
    return "period";
}

/** Returns nothing when either side predates settings being recorded, so it can't read as "everything was removed". */
function buildSettingsMetadataChanges(
    from: NormalizedVersion,
    to: NormalizedVersion,
): MetadataChange[] {
    if (!from.settings || !to.settings) return [];

    const changes: MetadataChange[] = [];
    const add = (key: string, label: string, before: string, after: string) => {
        if (before !== after) changes.push({ key, label, before, after });
    };

    add(
        "settings-name",
        "Model name",
        from.settings.name || NONE,
        to.settings.name || NONE,
    );
    add(
        "settings-description",
        "Description",
        from.settings.description || NONE,
        to.settings.description || NONE,
    );
    // Compare by id, not label: subgroups under different parents can share a name.
    if (from.settings.groupId !== to.settings.groupId) {
        changes.push({
            key: "settings-group",
            label: "Model group",
            before: from.settings.groupLabel,
            after: to.settings.groupLabel,
        });
    }
    add(
        "settings-visibility",
        "Visibility",
        from.settings.visibility,
        to.settings.visibility,
    );
    // Kept separate from Visibility so neither chip becomes a mode plus a role list.
    if (
        from.settings.visibility === "Specific roles" ||
        to.settings.visibility === "Specific roles"
    ) {
        add(
            "settings-roles",
            "Roles",
            from.settings.roleLabel,
            to.settings.roleLabel,
        );
    }

    return [...changes, ...buildOutputChanges(from.settings, to.settings)];
}

/** Matched by id (a changed result on the same id is a rename), falling back to matching by result if the id churned. */
function buildOutputChanges(
    from: NormalizedSettings,
    to: NormalizedSettings,
): MetadataChange[] {
    const changes: MetadataChange[] = [];
    const usedBefore = new Set<number>();

    const take = (
        predicate: (candidate: { id: string; result: string }) => boolean,
    ) => {
        const index = from.outputs.findIndex(
            (candidate, position) =>
                !usedBefore.has(position) && predicate(candidate),
        );
        if (index < 0) return undefined;
        usedBefore.add(index);
        return from.outputs[index];
    };

    for (const output of to.outputs) {
        const matched =
            take((candidate) => !!output.id && candidate.id === output.id) ??
            take((candidate) => candidate.result === output.result);

        if (!matched) {
            changes.push({
                key: `settings-output-added-${output.id || output.result}`,
                label: "Model output",
                before: NONE,
                after: output.result,
            });
            continue;
        }

        if (matched.result !== output.result) {
            changes.push({
                key: `settings-output-${output.id || output.result}`,
                label: "Model output",
                before: matched.result,
                after: output.result,
            });
        }
    }

    from.outputs.forEach((output, position) => {
        if (usedBefore.has(position)) return;
        changes.push({
            key: `settings-output-removed-${output.id || output.result}`,
            label: "Model output",
            before: output.result,
            after: NONE,
        });
    });

    return changes;
}

/** Shown as the full set on each side (not one row per added/removed parent) so a kept parent isn't implied away. */
function parentModelsLabel(parents: { name: string }[]): string {
    return parents.length
        ? [...parents]
            .map((parent) => parent.name)
            .sort((a, b) => a.localeCompare(b))
            .join(", ")
        : NONE;
}

function buildParentChanges(
    from: NormalizedVersion,
    to: NormalizedVersion,
): MetadataChange[] {
    const before = parentModelsLabel(from.parentModels);
    const after = parentModelsLabel(to.parentModels);
    if (before === after) return [];

    return [
        {
            key: "parent-models",
            label: "Parent models",
            before,
            after,
        },
    ];
}

function buildMetadataChanges(
    from: NormalizedVersion,
    to: NormalizedVersion,
    promptBlocks: PromptDiffBlock[],
): MetadataChange[] {
    const changes: MetadataChange[] = [
        ...buildSettingsMetadataChanges(from, to),
        ...buildParentChanges(from, to),
    ];
    const add = (key: string, label: string, before: string, after: string) => {
        if (before !== after) changes.push({ key, label, before, after });
    };

    add(
        "builderType",
        "Builder",
        builderLabel(from.type),
        builderLabel(to.type),
    );
    add("llmModel", "LLM model", from.llmModelLabel, to.llmModelLabel);
    add(
        "purpose",
        "Purpose",
        purposeLabel(from.llmPurpose),
        purposeLabel(to.llmPurpose),
    );
    add(
        "targetSpeaker",
        "Target speaker",
        from.llmTargetSpeaker ?? "Any",
        to.llmTargetSpeaker ?? "Any",
    );

    if (
        from.type === LLMClassifierImplementationConfigurationType.PromptOnly ||
        to.type === LLMClassifierImplementationConfigurationType.PromptOnly
    ) {
        add(
            "questionText",
            "Question text",
            from.questionText || NONE,
            to.questionText || NONE,
        );
    }

    return [...changes, ...buildStepMetadataChanges(from, to, promptBlocks)];
}

interface StepPairing {
    stepId: string;
    stepNumber: number;
    stepLabel: string;
    before?: NormalizedStep;
    after?: NormalizedStep;
}

/** Every step on either side, so an added or removed step reports its whole configuration, not just its prompt. */
function stepPairings(
    from: NormalizedVersion,
    to: NormalizedVersion,
    promptBlocks: PromptDiffBlock[],
): StepPairing[] {
    const labelByStepId = new Map(
        promptBlocks.map((block) => [block.key, block.label]),
    );

    // A builder change treats every prompt as an addition or removal. Use the
    // same prefixed identities here so the rest of each step's configuration
    // is rendered in the same change group as its prompt.
    if (from.type !== to.type) {
        return [
            ...to.steps.map((step) => ({
                stepId: `added-${step.id}`,
                stepNumber: step.index + 1,
                stepLabel:
                    labelByStepId.get(`added-${step.id}`) ?? step.name,
                before: undefined,
                after: step,
            })),
            ...from.steps.map((step) => ({
                stepId: `removed-${step.id}`,
                stepNumber: step.index + 1,
                stepLabel:
                    labelByStepId.get(`removed-${step.id}`) ?? step.name,
                before: step,
                after: undefined,
            })),
        ];
    }

    const { pairs, unmatchedBefore } = matchStepsWithRenames(
        from.steps,
        to.steps,
    );

    return [
        ...pairs.map(({ before, after }) => ({
            stepId: after.id,
            stepNumber: after.index + 1,
            stepLabel: labelByStepId.get(after.id) ?? after.name,
            before,
            after,
        })),
        ...unmatchedBefore.map((step) => ({
            stepId: step.id,
            stepNumber: step.index + 1,
            stepLabel: labelByStepId.get(step.id) ?? step.name,
            before: step,
            after: undefined,
        })),
    ];
}

/** A missing step reads as None, which a field's own default would misreport. */
function stepValue(
    step: NormalizedStep | undefined,
    read: (step: NormalizedStep) => string,
) {
    return step ? read(step) : NONE;
}

function buildStepMetadataChanges(
    from: NormalizedVersion,
    to: NormalizedVersion,
    promptBlocks: PromptDiffBlock[],
): MetadataChange[] {
    if (
        from.type !==
        LLMClassifierImplementationConfigurationType.LogicChaining &&
        to.type !== LLMClassifierImplementationConfigurationType.LogicChaining
    ) {
        return [];
    }

    const changes: MetadataChange[] = [];

    for (const pairing of stepPairings(from, to, promptBlocks)) {
        const { before: fromStep, after: toStep } = pairing;
        // Reader passed once so the missing-step fallback stays in one place.
        const addStep = (
            key: string,
            label: string,
            read: (step: NormalizedStep) => string,
        ) => {
            const before = stepValue(fromStep, read);
            const after = stepValue(toStep, read);
            if (before === after) return;

            changes.push({
                key: `${pairing.stepId}-${key}`,
                label,
                stepLabel: pairing.stepLabel,
                stepNumber: pairing.stepNumber,
                stepId: pairing.stepId,
                before,
                after,
            });
        };

        addStep(
            "model",
            "Model",
            (step) => step.llmModelName ?? "Model default",
        );
        addStep("temperature", "Temperature", (step) =>
            numberLabel(step.temperature),
        );
        addStep(
            "thinkingEffort",
            "Thinking effort",
            (step) => step.thinkingEffortLevel ?? "Off",
        );
        addStep("entryPoint", "Entry point", (step) =>
            step.isEntryPoint ? "Yes" : "No",
        );
        addStep("structuredOutputs", "Structured output", (step) =>
            step.useStructuredOutputs ? "On" : "Off",
        );
    }

    return changes;
}

/** Step actions, outputs and schemas apply only to chains; reported per step so an added/removed step's full config isn't lost. */
function quotaText(lines: string[]): string {
    return lines.length ? lines.join("\n") : NONE;
}

function buildConfigBlocks(
    from: NormalizedVersion,
    to: NormalizedVersion,
    promptBlocks: PromptDiffBlock[],
): ConfigDiffBlock[] {
    const blocks: ConfigDiffBlock[] = [];
    const add = (
        key: string,
        label: string,
        before: string,
        after: string,
        stepLabel?: string,
        stepNumber?: number,
        stepId?: string,
    ) => {
        if (before === after) return;
        blocks.push({
            key,
            label,
            stepLabel,
            stepNumber,
            stepId,
            beforeText: before,
            afterText: after,
            ...getLineDiffCounts(before, after),
        });
    };

    add(
        "runConditions",
        "Run conditions",
        structureText(from.runConditions),
        structureText(to.runConditions),
    );
    add(
        "metadataFilters",
        "Metadata filters",
        structureText(from.metadataFilters),
        structureText(to.metadataFilters),
    );

    // Quotas carry no stable identity, so they're compared as sorted text rather than matched up.
    if (from.settings && to.settings) {
        add(
            "volumeControl",
            "Volume control",
            quotaText(from.settings.quotaLines),
            quotaText(to.settings.quotaLines),
        );
    }

    if (
        from.type === LLMClassifierImplementationConfigurationType.PromptOnly ||
        to.type === LLMClassifierImplementationConfigurationType.PromptOnly
    ) {
        add(
            "questionDescription",
            "Question definition",
            from.questionDescription || NONE,
            to.questionDescription || NONE,
        );
        // Criteria, examples and colour changes are invisible in a name list.
        add(
            "resultDefinitions",
            "Result definitions",
            resultDefinitionsText(from.resultDefinitions),
            resultDefinitionsText(to.resultDefinitions),
        );
    }

    if (
        from.type !==
        LLMClassifierImplementationConfigurationType.LogicChaining &&
        to.type !== LLMClassifierImplementationConfigurationType.LogicChaining
    ) {
        return blocks;
    }

    for (const pairing of stepPairings(from, to, promptBlocks)) {
        const { before: fromStep, after: toStep, stepLabel } = pairing;
        const addStep = (
            key: string,
            label: string,
            before: string,
            after: string,
        ) =>
            add(
                `${pairing.stepId}-${key}`,
                label,
                before,
                after,
                stepLabel,
                pairing.stepNumber,
                pairing.stepId,
            );

        addStep(
            "actions",
            "Actions",
            structureText(fromStep?.actions),
            structureText(toStep?.actions),
        );
        addStep(
            "expectedOutputs",
            "Expected outputs",
            expectedOutputsText(fromStep?.expectedOutputs ?? []),
            expectedOutputsText(toStep?.expectedOutputs ?? []),
        );
        addStep(
            "structuredOutputSchema",
            "Structured output schema",
            structureText(fromStep?.structuredOutputJsonSchema),
            structureText(toStep?.structuredOutputJsonSchema),
        );
    }

    return blocks;
}

/** Keys are sorted because a JObject preserves the order it was saved in, which would otherwise read as a change. */
function structureText(value: unknown) {
    const stripped = stripVolatileNestedFields(value);
    if (stripped === undefined || stripped === null) return NONE;
    if (Array.isArray(stripped) && stripped.length === 0) return NONE;

    return JSON.stringify(stripped, sortedKeysReplacer, 2);
}

function sortedKeysReplacer(_key: string, value: unknown) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return value;
    }

    return Object.keys(value as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((result, key) => {
            result[key] = (value as Record<string, unknown>)[key];
            return result;
        }, {});
}

/** Order is preserved, since the editor presents these as an ordered list. */
function expectedOutputsText(
    outputs: ILLMExecutorStepPromptConfigurationOutputVariable[],
) {
    if (outputs.length === 0) return NONE;

    return outputs.map(expectedOutputLine).join("\n");
}

function expectedOutputLine(
    output: ILLMExecutorStepPromptConfigurationOutputVariable,
) {
    const isEnum =
        output.type ===
        LLMExecutorStepPromptConfigurationOutputVariableType.Enum;
    const type = isEnum
        ? `Enum(${(output.enumValues ?? []).join(", ")})`
        : "String";
    // The persisted field really is spelled "requried".
    const required = output.requried ? "required" : "optional";

    return `${output.name || "(unnamed)"} - ${type}, ${required}`;
}

function builderLabel(type?: LLMClassifierImplementationConfigurationType) {
    return type === LLMClassifierImplementationConfigurationType.LogicChaining
        ? "Advanced"
        : "Standard";
}

function purposeLabel(purpose?: LLMClassifierPurpose) {
    switch (purpose) {
        case LLMClassifierPurpose.Quality:
            return "Quality";
        case LLMClassifierPurpose.Insights:
            return "Insights";
        case LLMClassifierPurpose.ExtractData:
            return "Extract data";
        default:
            return NONE;
    }
}

/** Sorted by result since saved order isn't meaningful; rendered in full so a criteria/examples/color edit isn't dropped. */
function resultDefinitionsText(definitions: LLMResultDefinition[]) {
    if (definitions.length === 0) return NONE;

    return [...definitions]
        .sort((left, right) => left.result.localeCompare(right.result))
        .map((definition) => {
            const examples = (definition.examples ?? []).filter(
                (example) => example.trim().length > 0,
            );

            return [
                definition.result || "(unnamed)",
                `  criteria: ${definition.criteria?.trim() || NONE}`,
                `  examples: ${examples.length > 0 ? examples.join(" | ") : NONE
                }`,
                `  color: ${definition.color || NONE}`,
            ].join("\n");
        })
        .join("\n\n");
}

function numberLabel(value?: number | null) {
    return value === undefined || value === null ? "Default" : String(value);
}

function asText(value: unknown) {
    return typeof value === "string" ? value : "";
}

function sameName(left: string, right: string) {
    return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function slugBase(id: string) {
    return id.replace(/-[a-z0-9]{4}$/, "");
}
